<script setup lang="ts">
  import { computed, ref } from "vue";
  import {
    // NAlert,
    NCard,
    NSpace,
    NTag,
    NButton,
    NStatistic,
    NList,
    NListItem,
    NIcon,
    NProgress,
    NDivider,
    NText,
    NInputNumber,
    NTooltip,
    NSelect,
  } from "naive-ui";
  import { $t } from "@/locales";
  import { formatNumberWithCommas } from "@/utils/format";
  import PhoneVerify from "@/views/_builtin/login/modules/phone-verify.vue";
  // import { useAuthStore } from "@/store/modules/auth";
  // import { useUserStore } from "@/store/modules/user";

  interface Props {
    currentTier: string;
    totalPaidInUnit: number;
    exchangeRate: number;
    isMobile: boolean;
    direction: "ltr" | "rtl";
  }

  const props = defineProps<Props>();

  // // Store access for authentication status
  // const authStore = useAuthStore();
  // const userStore = useUserStore();

  const emit = defineEmits<{
    upgrade: [requiredAmount: number];
  }>();

  // User-editable IRT amount with smart defaults
  const customIrtAmount = ref<number | null>(null);

  // Target tier selection - defaults to next tier
  const selectedTargetTier = ref<string | null>(null);

  const tierRequirements = {
    tier0: 0,
    tier1: 0,
    tier2: 10,
    tier3: 50,
    tier4: 250,
    tier5: 1000,
  };

  const tierBenefits: Record<string, string[]> = {
    tier0: ["limits.benefits.tier0.basic", "limits.benefits.tier0.limited"],
    tier1: [
      "limits.benefits.tier1.increased",
      "limits.benefits.tier1.priority",
      "limits.benefits.tier1.models",
    ],
    tier2: [
      "limits.benefits.tier2.higher",
      "limits.benefits.tier2.advanced",
      "limits.benefits.tier2.support",
    ],
    tier3: [
      "limits.benefits.tier3.premium",
      "limits.benefits.tier3.enhanced",
      "limits.benefits.tier3.features",
    ],
    tier4: [
      "limits.benefits.tier4.enterprise",
      "limits.benefits.tier4.maximum",
      "limits.benefits.tier4.dedicated",
    ],
    tier5: [
      "limits.benefits.tier5.unlimited",
      "limits.benefits.tier5.all",
      "limits.benefits.tier5.priority",
    ],
  };

  const currentTierNumber = computed(() => {
    const lastChar = props.currentTier.slice(-1);
    return Number(lastChar) || 0;
  });

  // Check if user is in tier 0 (unverified/unauthenticated)
  // const isUnverifiedUser = computed(() => {
  //   return currentTierNumber.value === 0;
  // });

  // // Determine the specific reason for tier 0 status
  // const unverifiedReason = computed(() => {
  //   if (!authStore.isLogin) {
  //     return "not_logged_in";
  //   }
  //   if (!userStore.userInfo.phone || userStore.userInfo.phone.trim() === "") {
  //     return "phone_not_verified";
  //   }
  //   if (!userStore.isUserAuthenticated()) {
  //     return "not_authenticated";
  //   }
  //   return "unknown";
  // });

  // Get appropriate message for the unverified state
  // const unverifiedMessage = computed(() => {
  //   switch (unverifiedReason.value) {
  //     case "not_logged_in":
  //       return {
  //         title: $t("limits.notLoggedIn"),
  //         description: $t("limits.loginRequired"),
  //         action: $t("limits.loginNow"),
  //       };
  //     case "phone_not_verified":
  //       return {
  //         title: $t("limits.phoneNotVerified"),
  //         description: $t("limits.phoneVerificationRequired"),
  //         action: $t("limits.verifyPhone"),
  //       };
  //     case "not_authenticated":
  //       return {
  //         title: $t("limits.notAuthenticated"),
  //         description: $t("limits.authenticationRequired"),
  //         action: $t("limits.authenticateNow"),
  //       };
  //     default:
  //       return {
  //         title: $t("limits.accessRestricted"),
  //         description: $t("limits.verificationRequired"),
  //         action: $t("limits.getVerified"),
  //       };
  //   }
  // });

  const isTier0ToTier1Upgrade = computed(() => props.currentTier === "tier0");

  const nextTier = computed(() => {
    if (currentTierNumber.value >= 5) return null;
    return `tier${currentTierNumber.value + 1}`;
  });

  // Available target tiers for upgrade
  const availableTargetTiers = computed(() => {
    const options = [];
    for (let i = currentTierNumber.value + 1; i <= 5; i++) {
      const tierKey = `tier${i}`;
      const requirement =
        tierRequirements[tierKey as keyof typeof tierRequirements];
      const remaining = Math.max(0, requirement - props.totalPaidInUnit);
      const baseIrtCost = remaining * props.exchangeRate * 1.01;
      const irtCost = Math.ceil(baseIrtCost / 1000) * 1000; // Round up to nearest 1000

      const tierName = getCurrentTierName(tierKey);
      const isNext = tierKey === nextTier.value;
      const recommendedText = isNext ? ` (${$t("limits.recommended")})` : "";
      const detailsText =
        tierKey === "tier1"
          ? $t("limits.phoneVerificationRequired")
          : `${$t("limits.required")}: ${requirement} ${$t("billing.UNIT")} • ${$t("limits.cost")}: ${irtCost.toLocaleString()} ${$t("billing.IRT")}`;

      options.push({
        value: tierKey,
        label: `${$t("limits.usageTier")} ${tierName}${recommendedText} - ${detailsText}`,
        requirement,
        remaining,
        irtCost,
        isNext: tierKey === nextTier.value,
      });
    }
    return options;
  });

  // Selected target tier (defaults to next tier)
  const targetTier = computed(() => {
    return selectedTargetTier.value ?? nextTier.value;
  });

  const targetTierRequirement = computed(() => {
    if (!targetTier.value) return 0;
    return tierRequirements[targetTier.value as keyof typeof tierRequirements];
  });

  const remainingAmount = computed(() => {
    if (!targetTier.value) return 0;
    return Math.max(0, targetTierRequirement.value - props.totalPaidInUnit);
  });

  // Calculate suggested IRT amount with 1% buffer and rounding up to nearest 1000
  const suggestedIrtAmount = computed(() => {
    if (remainingAmount.value <= 0) return 0;
    // Add 1% buffer for exchange rate fluctuations and round up to nearest 1000
    const withBuffer = remainingAmount.value * props.exchangeRate * 1.01;
    return Math.ceil(withBuffer / 1000) * 1000;
  });

  // Use custom IRT amount if set, otherwise use suggested IRT amount
  const selectedIrtAmount = computed(() => {
    return customIrtAmount.value ?? suggestedIrtAmount.value;
  });

  // Calculate the UNIT equivalent of the selected IRT amount
  const selectedUnitAmount = computed(() => {
    return selectedIrtAmount.value / props.exchangeRate;
  });

  // Helper to reset to suggested amount
  const resetToSuggested = () => {
    customIrtAmount.value = null;
  };

  // Helper to set custom IRT amount
  const setCustomIrtAmount = (value: number | null) => {
    customIrtAmount.value = value;
  };

  // Helper to reset target tier to next tier
  const resetToNextTier = () => {
    selectedTargetTier.value = null;
    customIrtAmount.value = null;
  };

  const progressToNextTier = computed(() => {
    if (!nextTier.value) return 100; // If there's no next tier, progress is complete.

    const currentRequirement =
      tierRequirements[props.currentTier as keyof typeof tierRequirements];

    // Validate currentRequirement and targetTierRequirement to avoid errors
    if (
      !isFinite(currentRequirement) ||
      !isFinite(targetTierRequirement.value) ||
      targetTierRequirement.value <= currentRequirement
    ) {
      return 0; // Return 0 progress if requirements are invalid or progress is impossible.
    }

    const progress =
      ((props.totalPaidInUnit - currentRequirement) /
        (targetTierRequirement.value - currentRequirement)) *
      100;

    // Ensure progress is clamped between 0 and 100
    return Math.min(100, Math.max(0, progress));
  });
  const getCurrentTierName = (tier: string) => {
    const tierNames: Record<string, string> = {
      tier0: $t("limits.free"),
      tier1: "1",
      tier2: "2",
      tier3: "3",
      tier4: "4",
      tier5: "5",
    };
    return tierNames[tier] || tier;
  };

  const handleUpgrade = () => {
    if (isTier0ToTier1Upgrade.value) return;

    if (selectedUnitAmount.value > 0) {
      emit("upgrade", selectedUnitAmount.value);
    }
  };
</script>

<template>
  <NSpace vertical :size="16" :style="{ direction: direction }">
    <!-- Current Tier Info -->
    <NCard size="small">
      <template #header>
        <NSpace align="center" :size="16">
          <NTag
            :type="currentTier === 'tier5' ? 'success' : 'info'"
            size="large"
            round
          >
            {{ $t("limits.usageTier") }} {{ getCurrentTierName(currentTier) }}
          </NTag>
          <NText depth="3">{{ $t("limits.currentTier") }}</NText>
        </NSpace>
      </template>

      <NSpace vertical :size="16">
        <div>
          <NText strong>{{ $t("limits.currentBenefits") }}</NText>
          <NList style="margin-top: 8px">
            <NListItem
              v-for="benefit in tierBenefits[currentTier] || []"
              :key="benefit"
            >
              <template #prefix>
                <NIcon color="#52c41a" size="16">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                    />
                  </svg>
                </NIcon>
              </template>
              <NText>{{ $t(benefit) }}</NText>
            </NListItem>
          </NList>
        </div>
      </NSpace>
    </NCard>

    <!-- Phone Verification Upgrade (tier0 -> tier1) -->
    <NCard
      v-if="isTier0ToTier1Upgrade"
      size="small"
      class="phone-upgrade-card"
      :content-style="{ padding: isMobile ? '14px' : '20px' }"
    >
      <template #header>
        <NSpace class="phone-upgrade-header" align="center" justify="space-between" :size="12">
          <div class="phone-upgrade-title">
            <NTag type="success" :size="isMobile ? 'medium' : 'large'" round>
              {{ $t("limits.usageTier") }} 1
            </NTag>
            <NText depth="3">{{ $t("limits.nextTier") }}</NText>
          </div>
          <NText class="phone-upgrade-free" type="success" strong>
            {{ $t("limits.phoneVerified") }}
          </NText>
        </NSpace>
      </template>

      <div class="phone-upgrade-body">
        <PhoneVerify
          :direction="direction"
          :message="$t('limits.tier1PhoneVerificationUpgrade')"
          :show-back-button="false"
          compact
        />
      </div>
    </NCard>

    <!-- Tier Upgrade Options (if not at max tier) -->
    <NCard size="small" v-else-if="availableTargetTiers.length > 0">
      <template #header>
        <NSpace align="center" :size="16">
          <NTag
            :type="selectedTargetTier ? 'success' : 'warning'"
            size="large"
            round
          >
            {{ $t("limits.usageTier") }}
            {{ getCurrentTierName(targetTier || "tier1") }}
          </NTag>
          <NText depth="3">{{
            selectedTargetTier
              ? $t("limits.selectedTier")
              : $t("limits.nextTier")
          }}</NText>
        </NSpace>
      </template>

      <NSpace vertical :size="20">
        <!-- Tier Selection -->
        <div>
          <NSpace
            justify="space-between"
            align="center"
            style="margin-bottom: 12px"
          >
            <NText strong>{{ $t("limits.selectTargetTier") }}</NText>
            <NButton
              v-if="selectedTargetTier"
              size="small"
              quaternary
              @click="resetToNextTier"
            >
              {{ $t("limits.resetToNext") }}
            </NButton>
          </NSpace>
          <NSelect
            v-model:value="selectedTargetTier"
            :options="availableTargetTiers"
            :placeholder="$t('limits.selectTierPlaceholder')"
            size="large"
            label-field="label"
            value-field="value"
          />
        </div>

        <!-- Progress Section -->
        <div>
          <NSpace justify="space-between" style="margin-bottom: 12px">
            <NText strong>{{ $t("limits.progress") }}</NText>
            <NText>{{ progressToNextTier.toFixed(1) }}%</NText>
          </NSpace>
          <NProgress
            :percentage="progressToNextTier"
            :show-indicator="false"
            :height="8"
            border-radius="4px"
          />
        </div>

        <!-- Statistics -->
        <NSpace :size="16" :wrap="isMobile">
          <NStatistic
            :label="$t('limits.totalPaid')"
            :value="formatNumberWithCommas(totalPaidInUnit, 2)"
            :precision="2"
          >
            <template #suffix
              ><span class="text-xs mx-1"
                >({{ $t("common.equivalent") }} {{ $t("billing.UNIT") }})</span
              ></template
            >
          </NStatistic>

          <NStatistic
            :label="$t('limits.required')"
            :value="formatNumberWithCommas(targetTierRequirement, 2)"
          >
            <template #suffix
              ><span class="text-xs mx-1"
                >({{ $t("common.equivalent") }} {{ $t("billing.UNIT") }})</span
              ></template
            >
          </NStatistic>

          <NStatistic
            :label="$t('limits.remaining')"
            :value="formatNumberWithCommas(remainingAmount, 2)"
            :value-style="{
              color: remainingAmount > 0 ? '#fa8c16' : '#52c41a',
            }"
          >
            <template #suffix
              ><span class="text-xs mx-1"
                >({{ $t("common.equivalent") }} {{ $t("billing.UNIT") }})</span
              ></template
            >
          </NStatistic>
        </NSpace>

        <NDivider />

        <!-- Target Tier Benefits -->
        <div>
          <NText strong>{{
            selectedTargetTier
              ? $t("limits.targetTierBenefits")
              : $t("limits.nextTierBenefits")
          }}</NText>
          <NList style="margin-top: 8px">
            <NListItem
              v-for="benefit in tierBenefits[targetTier || 'tier1'] || []"
              :key="benefit"
            >
              <template #prefix>
                <NIcon color="#1890ff" size="16">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    />
                  </svg>
                </NIcon>
              </template>
              <NText>{{ $t(benefit) }}</NText>
            </NListItem>
          </NList>
        </div>

        <NDivider />

        <!-- Upgrade Action -->
        <div
          style="
            background: var(--n-color-embedded);
            border-radius: 12px;
            padding: 20px;
            border: 1px solid var(--n-border-color);
          "
        >
          <NSpace vertical :size="20">
            <!-- Upgrade Header -->
            <div style="text-align: center">
              <NIcon
                size="32"
                color="var(--n-color-primary)"
                style="margin-bottom: 8px"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>
              </NIcon>
              <div>
                <NText
                  strong
                  style="font-size: 16px; display: block; margin-bottom: 4px"
                >
                  {{
                    selectedTargetTier
                      ? $t("limits.upgradeToSelectedTier")
                      : $t("limits.upgradeToNextTier")
                  }}
                </NText>
                <NText depth="3" style="font-size: 14px">
                  {{ $t("limits.info1") }}
                </NText>
              </div>
            </div>

            <!-- Quick Tier Selection in Header -->
            <div
              v-if="availableTargetTiers.length > 1"
              style="max-width: 300px; margin: 0 auto"
            >
              <NText
                depth="3"
                style="
                  font-size: 12px;
                  text-align: center;
                  display: block;
                  margin-bottom: 8px;
                "
              >
                {{ $t("limits.selectTargetTier") }}
              </NText>
              <NSelect
                v-model:value="selectedTargetTier"
                :options="availableTargetTiers"
                :placeholder="$t('limits.selectTierPlaceholder')"
                size="medium"
                label-field="label"
                value-field="value"
              />
            </div>

            <!-- Pricing Information -->
            <div
              style="
                background: var(--n-color);
                border-radius: 8px;
                padding: 16px;
              "
            >
              <NSpace vertical :size="16">
                <!-- Amount Selection -->
                <div>
                  <NSpace
                    justify="space-between"
                    align="center"
                    style="margin-bottom: 12px"
                  >
                    <NText strong style="font-size: 14px">{{
                      $t("limits.selectAmount")
                    }}</NText>
                    <NTooltip v-if="customIrtAmount === null">
                      <template #trigger>
                        <NText depth="3" style="font-size: 12px; cursor: help">
                          {{ $t("limits.suggestedAmount") }} (+1%)
                        </NText>
                      </template>
                      {{ $t("limits.suggestedAmountTooltip") }}
                    </NTooltip>
                  </NSpace>

                  <NSpace vertical :size="8">
                    <!-- IRT Amount Input -->
                    <NInputNumber
                      v-model:value="customIrtAmount"
                      :placeholder="suggestedIrtAmount.toString()"
                      :min="Math.ceil(remainingAmount * exchangeRate)"
                      :step="100000"
                      :precision="0"
                      size="large"
                      style="width: 100%"
                    >
                      <template #suffix>
                        <NText style="color: var(--n-text-color-disabled)">{{
                          $t("billing.IRT")
                        }}</NText>
                      </template>
                    </NInputNumber>

                    <!-- Quick Actions -->
                    <NSpace :size="8" style="width: 100%">
                      <NButton
                        v-if="customIrtAmount !== null"
                        size="small"
                        quaternary
                        @click="resetToSuggested"
                        style="flex: 1"
                      >
                        {{ $t("limits.useSuggested") }}
                      </NButton>
                      <NButton
                        size="small"
                        quaternary
                        @click="
                          setCustomIrtAmount(
                            Math.ceil(
                              (remainingAmount * exchangeRate * 1.1) / 1000
                            ) * 1000
                          )
                        "
                        style="flex: 1"
                      >
                        +10%
                      </NButton>
                      <NButton
                        size="small"
                        quaternary
                        @click="
                          setCustomIrtAmount(
                            Math.ceil(
                              (remainingAmount * exchangeRate * 1.25) / 1000
                            ) * 1000
                          )
                        "
                        style="flex: 1"
                      >
                        +25%
                      </NButton>
                    </NSpace>
                  </NSpace>
                </div>

                <NDivider style="margin: 8px 0" />

                <!-- Selected IRT Amount Display -->
                <div style="text-align: center">
                  <NText
                    depth="3"
                    style="
                      font-size: 12px;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                  >
                    {{ $t("billing.totalCost") }}
                  </NText>
                  <div style="margin: 4px 0">
                    <NText
                      strong
                      style="font-size: 24px; color: var(--n-color-primary)"
                    >
                      {{ selectedIrtAmount.toLocaleString() }}
                    </NText>
                    <NText style="font-size: 14px; margin-left: 4px">
                      {{ $t("billing.IRT") }}
                    </NText>
                  </div>
                  <NText depth="3" style="font-size: 11px">
                    {{ $t("limits.minimumRequired") }}:
                    {{
                      (
                        Math.ceil((remainingAmount * exchangeRate) / 1000) *
                        1000
                      ).toLocaleString()
                    }}
                    {{ $t("billing.IRT") }}
                  </NText>
                </div>

                <NDivider style="margin: 8px 0" />

                <!-- UNIT Equivalent Display -->
                <div style="text-align: center">
                  <NText
                    depth="3"
                    style="
                      font-size: 12px;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                  >
                    {{ $t("limits.selectedAmount") }}
                  </NText>
                  <div style="margin: 4px 0">
                    <NText
                      strong
                      style="font-size: 20px; color: var(--n-color-success)"
                    >
                      {{
                        selectedUnitAmount
                          ? selectedUnitAmount.toFixed(2)
                          : "0.00"
                      }}
                    </NText>
                    <NText style="font-size: 10px; margin-left: 4px">
                      ({{ $t("common.equivalent") }} {{ $t("billing.UNIT") }})
                    </NText>
                  </div>
                  <NText depth="3" style="font-size: 11px">
                    {{ $t("limits.exchangeRate") }}: 1
                    {{ $t("billing.UNIT") }} =
                    {{ exchangeRate.toLocaleString() }} {{ $t("billing.IRT") }}
                  </NText>
                </div>
              </NSpace>
            </div>

            <!-- Upgrade Benefits Preview -->
            <div
              style="
                background: var(--n-color-success);
                border-radius: 8px;
                padding: 12px;
                border: 1px solid var(--n-color-success);
              "
            >
              <NSpace align="center" :size="8" style="text-align: center">
                <NIcon size="16" color="var(--n-color-success)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                    />
                  </svg>
                </NIcon>
                <NText style="font-size: 13px; color: var(--n-color-success)">
                  {{ $t("limits.unlockBenefits") }}
                </NText>
              </NSpace>
            </div>

            <!-- Upgrade Button -->
            <NButton
              type="primary"
              size="large"
              round
              block
              :disabled="selectedUnitAmount <= 0"
              @click="handleUpgrade"
              style="height: 48px; font-weight: 600; font-size: 16px"
            >
              <template #icon>
                <NIcon size="20">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"
                    />
                  </svg>
                </NIcon>
              </template>
              {{ $t("limits.upgradeNow") }}
            </NButton>

            <!-- Additional Info -->
            <div style="text-align: center">
              <NText depth="3" style="font-size: 12px">
                {{ $t("limits.instantUpgrade") }}
              </NText>
            </div>
          </NSpace>
        </div>
      </NSpace>
    </NCard>

    <!-- Max Tier Reached -->
    <NCard size="small" v-else-if="currentTierNumber >= 5">
      <NSpace vertical :size="16" style="text-align: center; padding: 24px">
        <NIcon size="48" color="gold">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            />
          </svg>
        </NIcon>
        <NText strong style="font-size: 18px">{{
          $t("limits.maxTierReached")
        }}</NText>
        <NText depth="3">{{ $t("limits.maxTierDescription") }}</NText>
      </NSpace>
    </NCard>
  </NSpace>
</template>

<style scoped>
  .phone-upgrade-card {
    overflow: hidden;
  }

  .phone-upgrade-header {
    width: 100%;
  }

  .phone-upgrade-title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .phone-upgrade-free {
    white-space: nowrap;
    font-size: 12px;
  }

  .phone-upgrade-body {
    border: 1px solid var(--n-border-color);
    border-radius: 12px;
    background: var(--n-color-embedded);
    padding: 16px;
  }

  @media (max-width: 640px) {
    .phone-upgrade-header {
      align-items: flex-start !important;
      flex-direction: column;
    }

    .phone-upgrade-title {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .phone-upgrade-free {
      font-size: 11px;
    }

    .phone-upgrade-body {
      border-radius: 10px;
      padding: 12px;
    }
  }
</style>
